import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import { useTheme } from './contexts/ThemeContext';

// Pages
import Cadastro from './pages/Cadastro';
import Login from './pages/Login';
import EsqueciSenha from './pages/EsqueciSenha';
import TokenExpirado from './pages/TokenExpirado';
import RedefinirSenha from './pages/RedefinirSenha';
import Vitrine from './pages/Vitrine';
import NotFound from './pages/NotFound';
import CourseBoasVindas from './pages/Courses/BoasVindas';
import GenericCourse from './pages/Courses/GenericCourse';

import { supabase } from './services/supabaseClient';
import SvgIcons from './pages/Vitrine/v2/components/SvgIcons';

const App: React.FC = () => {
  const { darkMode } = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_user, setUser] = React.useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_loading, setLoading] = React.useState(true);

  useEffect(() => {
    // Verificar se há um usuário no localStorage antes de qualquer coisa
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        console.log('✅ App: Usuário encontrado no localStorage');
        setLoading(false);
      }
    } catch (e) {
      console.warn('⚠️ App: Erro ao acessar localStorage:', e);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: string, session: any) => {
      console.log('🔄 App: Evento de autenticação:', event);
      
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        setUser(session?.user ?? null);
        console.log('✅ App: Usuário atualizado após evento:', event);
      } else if (event === 'USER_UPDATED') {
        // Handle email verification
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        console.log('✅ App: Usuário atualizado após verificação de e-mail');
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        console.log('🚪 App: Usuário desconectado');
      }
      setLoading(false);
    });

    // Verificar sessão atual
    const checkCurrentSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('✅ App: Sessão ativa encontrada');
          setUser(session.user);
        } else {
          console.log('⚠️ App: Nenhuma sessão ativa encontrada');
          
          // Se não há sessão, verificar se há um usuário no localStorage
          try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              console.log('⚠️ App: Nenhuma sessão, mas usuário encontrado no localStorage');
              // Não limpa o usuário para evitar perda de sessão entre ambientes
            } else {
              setUser(null);
            }
          } catch (e) {
            console.warn('⚠️ App: Erro ao acessar localStorage:', e);
            setUser(null);
          }
        }
        setLoading(false);
      } catch (e) {
        console.error('❌ App: Erro ao verificar sessão:', e);
        setLoading(false);
      }
    };

    checkCurrentSession();

    // Check for token expiration in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('error_code') === '400' && params.get('error_description')?.includes('token')) {
      window.location.href = '/token-expirado';
      return;
    }

    // Verificar se estamos em um ambiente móvel e acessando um item específico
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isItemPage = window.location.pathname.includes('/area/produto/item/');
    
    if (isMobile && isItemPage) {
      console.log('📱 App: Detectado acesso a item via dispositivo móvel');
      
      // Verificar se estamos acessando de um ambiente diferente
      const isLocalhost = window.location.hostname === 'localhost';
      const isProd = !isLocalhost;
      
      if (isProd) {
        console.log('🌐 App: Acessando de ambiente de produção');
        
        // Forçar verificação adicional para evitar problemas de sessão
        try {
          const storedUser = localStorage.getItem('user');
          if (!storedUser) {
            console.log('⚠️ App: Nenhum usuário encontrado no localStorage, verificando sessão...');
            checkCurrentSession();
          }
        } catch (e) {
          console.warn('⚠️ App: Erro ao acessar localStorage:', e);
        }
      }
    }

    return () => subscription.unsubscribe();
  }, []);

  // Garante que o body também receba/remova a classe dark-mode e stylesheet da vitrine
  useEffect(() => {
    const id = 'dark-styles';
    if (darkMode) {
      document.body.classList.add('dark-mode');
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = '/vitrine_assets/styles-dark.css';
        document.head.appendChild(link);
      }
    } else {
      document.body.classList.remove('dark-mode');
      const darkLink = document.getElementById(id);
      if (darkLink) darkLink.remove();
    }
  }, [darkMode]);

  return (
    <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
      <Routes>
        <Route path="/cadastrar" element={<Cadastro />} />
        <Route path="/login" element={<Login />} />
        <Route path="/esqueci-senha" element={<EsqueciSenha />} />
        <Route path="/token-expirado" element={<TokenExpirado />} />
        <Route path="/redefinir-senha" element={<RedefinirSenha />} />
        <Route path="/vitrine" element={
          <ProtectedRoute>
            <Vitrine />
          </ProtectedRoute>
        } />
        <Route path="/area/produto/secao/:secaoId" element={
          <ProtectedRoute>
            <CourseBoasVindas />
          </ProtectedRoute>
        } />
        {/* Página genérica para cada item da seção (replica Boas-Vindas) */}
        <Route path="/area/produto/item/:itemId" element={
          <ProtectedRoute>
            <GenericCourse />
          </ProtectedRoute>
        } />
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <SvgIcons />
    </div>
  );
};

export default App;
